import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import settingsAtom from '../../../atoms/settings/SettingsAtom';
import { useAtom } from 'jotai';

const isMonday = (date) => {
  return date.getDay() === 1;
};

const ConstructionProgressPopup = ({ block, onSubmit, onCancel }) => {
  const [startWeek, setStartWeek] = useState(null);
  const [endWeek, setEndWeek] = useState(null);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [, setHighlightedBlocks] = useState([]);
  const [settingsData] = useAtom(settingsAtom);
  const { projectList } = settingsData;
  const currentProject = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));
  const timezone = currentProject?.timezone || 'Pacific/Auckland';

  useEffect(() => {
    if (block?.existingSequence) {
      const existing = block.existingSequence;
      const startDate = existing.startWeek ? dayjs.unix(existing.startWeek).tz(timezone).toDate() : null;
      const endDate = existing.endWeek ? dayjs.unix(existing.endWeek).tz(timezone).toDate() : null;
      setStartWeek(startDate);
      setEndWeek(endDate);
      setSelectedSequence(existing);
    } else {
      setStartWeek(null);
      setEndWeek(null);
      setSelectedSequence(null);
    }

    if (block?.allSequences) {
      const sequenceBlocks = block.allSequences.map(seq => seq.id);
      setHighlightedBlocks(sequenceBlocks);
    }
    // eslint-disable-next-line
  }, [block]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startWeek || !endWeek) return;

    const startTimestamp = dayjs(startWeek).tz(timezone).startOf('day').unix();
    const endTimestamp = dayjs(endWeek).tz(timezone).endOf('day').unix();

    const payload = {
      startWeek: startTimestamp,
      endWeek: endTimestamp,
      sequenceId: selectedSequence?.id || null,
      blockId: block.id,
    };

    if (selectedSequence?.id) {
      payload.id = selectedSequence.id;
    }

    onSubmit(payload);

    setStartWeek(null);
    setEndWeek(null);
    setSelectedSequence(null);
  };

  const handleCancel = () => {
    setStartWeek(null);
    setEndWeek(null);
    setSelectedSequence(null);
    onCancel();
  };

  if (!block) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 350 }}>
        <Typography variant="h6">
          {block.name || `Block ${block.id}`}
        </Typography>
        
        <DatePicker
          label="Start Week"
          value={startWeek}
          onChange={(newValue) => {
            setStartWeek(newValue);
            if (endWeek && newValue && newValue >= endWeek) {
              setEndWeek(null);
            }
          }}
          shouldDisableDate={date => !isMonday(date)}
          format="dd-MM-yyyy"
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
            }
          }}
        />
        <DatePicker
          label="End Week"
          value={endWeek}
          onChange={setEndWeek}
          shouldDisableDate={date => !isMonday(date)}
          minDate={startWeek || undefined}
          format="dd-MM-yyyy"
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleCancel} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!startWeek || !endWeek || (endWeek <= startWeek)}
          >
            {selectedSequence ? `Update Sequence` : 'Add New Sequence'}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default ConstructionProgressPopup;